/**
 * Shared chart legend component with multi-row wrapping.
 * Replaces both InlineLegend (rect swatches) and the hand-rolled
 * line-chart legend (line + marker swatches) with a single system
 * that never truncates labels.
 *
 * Swatch types:
 *   'rect'   – colored square  (bar, stacked, heatmap)
 *   'line'   – line + marker   (line chart)
 *   'marker' – marker shape    (scatter plot)
 *
 * item shape:
 *   { key, label, color, type, dash?, markerShape?, hatchIndex? }
 */

import { computeLegendRows, LEGEND_SPACING } from '../../utils/chartLayout';
import { chartFonts } from '../../utils/chartFonts';
import { HatchPatternDefs } from './HatchPatterns';

export function renderLegendMarker({ shape, x, y, size, fill, stroke }) {
  const s = size;
  const props = { fill, stroke, strokeWidth: 1 };
  if (shape === 'square') return <rect x={x - s} y={y - s} width={s * 2} height={s * 2} {...props} />;
  if (shape === 'triangle') return <polygon points={`${x},${y - s} ${x - s},${y + s} ${x + s},${y + s}`} {...props} />;
  if (shape === 'diamond') return <polygon points={`${x},${y - s} ${x - s},${y} ${x},${y + s} ${x + s},${y}`} {...props} />;
  if (shape === 'triangleDown') return <polygon points={`${x - s},${y - s} ${x + s},${y - s} ${x},${y + s}`} {...props} />;
  if (shape === 'plus') {
    const t = s * 0.35;
    return <polygon points={`${x - t},${y - s} ${x + t},${y - s} ${x + t},${y - t} ${x + s},${y - t} ${x + s},${y + t} ${x + t},${y + t} ${x + t},${y + s} ${x - t},${y + s} ${x - t},${y + t} ${x - s},${y + t} ${x - s},${y - t} ${x - t},${y - t}`} {...props} />;
  }
  if (shape === 'cross') {
    const a = s * 0.7, t = s * 0.25;
    return <polygon points={`${x},${y - t} ${x + a - t},${y - a} ${x + a},${y - a + t} ${x + t},${y} ${x + a},${y + a - t} ${x + a - t},${y + a} ${x},${y + t} ${x - a + t},${y + a} ${x - a},${y + a - t} ${x - t},${y} ${x - a},${y - a + t} ${x - a + t},${y - a}`} {...props} />;
  }
  if (shape === 'hexagon') {
    const pts = [0, 1, 2, 3, 4, 5].map(i => { const a = Math.PI / 3 * i - Math.PI / 6; return `${x + s * Math.cos(a)},${y + s * Math.sin(a)}`; }).join(' ');
    return <polygon points={pts} {...props} />;
  }
  if (shape === 'pentagon') {
    const pts = [0, 1, 2, 3, 4].map(i => { const a = (2 * Math.PI / 5) * i - Math.PI / 2; return `${x + s * Math.cos(a)},${y + s * Math.sin(a)}`; }).join(' ');
    return <polygon points={pts} {...props} />;
  }
  if (shape === 'star') {
    const pts = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(i => { const a = (Math.PI / 5) * i - Math.PI / 2; const r = i % 2 === 0 ? s : s * 0.45; return `${x + r * Math.cos(a)},${y + r * Math.sin(a)}`; }).join(' ');
    return <polygon points={pts} {...props} />;
  }
  return <circle cx={x} cy={y} r={s} {...props} />;
}

function LegendSwatch({ item, theme, markerScale }) {
  const rs = Math.round(LEGEND_SPACING.rectSize * markerScale);
  if (item.type === 'line') {
    const ll = Math.round(LEGEND_SPACING.lineLength * markerScale);
    const lms = Math.round(LEGEND_SPACING.lineMarkerSize * markerScale);
    return (
      <>
        <line x1={0} y1={0} x2={ll} y2={0} stroke={item.color} strokeWidth={2} strokeDasharray={item.dash || '0'} />
        {renderLegendMarker({ shape: item.markerShape || 'circle', x: Math.round(ll / 2), y: 0, size: lms, fill: item.color, stroke: theme.axisColor })}
      </>
    );
  }
  if (item.type === 'marker') {
    const ms = Math.round(LEGEND_SPACING.markerSize * markerScale);
    return renderLegendMarker({ shape: item.markerShape || 'circle', x: Math.round(rs / 2), y: 0, size: ms, fill: item.color, stroke: theme.axisColor });
  }
  return (
    <rect x={0} y={-Math.round(rs / 2)} width={rs} height={rs} fill={item.color} />
  );
}

function textXForType(type, markerScale) {
  if (type === 'line') return Math.round(LEGEND_SPACING.lineTextX * markerScale);
  if (type === 'marker') return Math.round(LEGEND_SPACING.markerTextX * markerScale);
  return Math.round(LEGEND_SPACING.rectTextX * markerScale);
}

/**
 * ChartLegend – wrapping, multi-row legend for any chart type.
 *
 * Props:
 *   items       – [{ key, label, color, type, dash?, markerShape?, hatchIndex? }]
 *   innerWidth  – available width for wrapping (plot inner width)
 *   theme       – resolved theme object
 *   position    – 'top' | 'bottom' | 'right'
 *   transform   – optional SVG transform for the outer <g>
 *   markerScale – multiplier on default marker/swatch sizes (default 1)
 */
function ChartLegend({ items, innerWidth, theme, position = 'top', transform, markerScale: markerScaleProp }) {
  if (!items || items.length === 0) return null;

  const fonts = chartFonts(theme);
  const legendFontSize = theme.legendSize ?? 12;
  const markerScale = markerScaleProp ?? (theme.legendMarkerSize != null ? theme.legendMarkerSize / LEGEND_SPACING.rectSize : 1);
  const rs = Math.round(LEGEND_SPACING.rectSize * markerScale);

  const hasHatch = items.some((item) => item.hatchIndex != null);
  const hatchPrefix = 'chart-legend-hatch';

  if (position === 'right') {
    return (
      <g transform={transform}>
        {hasHatch && <HatchPatternDefs idPrefix={hatchPrefix} colors={items.map((i) => i.color)} />}
        {items.map((item, i) => (
          <g key={item.key} transform={`translate(0, ${i * LEGEND_SPACING.rightItemHeight})`}>
            <LegendSwatch item={item} theme={theme} markerScale={markerScale} />
            {item.hatchIndex != null && item.type === 'rect' && (
              <rect x={0} y={-Math.round(rs / 2)} width={rs} height={rs} fill={`url(#${hatchPrefix}-${i})`} />
            )}
            <text x={textXForType(item.type, markerScale)} y={LEGEND_SPACING.textDy} fill={theme.legendTextColor} fontSize={legendFontSize} fontFamily={fonts.label}>
              {item.label}
            </text>
          </g>
        ))}
      </g>
    );
  }

  const { rows, itemWidths } = computeLegendRows(items, innerWidth, legendFontSize, markerScale);
  const rowHeight = LEGEND_SPACING.rowHeight;

  return (
    <g transform={transform}>
      {hasHatch && <HatchPatternDefs idPrefix={hatchPrefix} colors={items.map((i) => i.color)} />}
      {rows.map((row, rowIdx) => {
        const rowWidth = row.items.reduce((s, j) => s + itemWidths[j], 0);
        const rowOffsetX = Math.max(0, (innerWidth - rowWidth) / 2);
        return (
        <g key={rowIdx} transform={`translate(${rowOffsetX}, ${rowIdx * rowHeight})`}>
          {row.items.map((itemIdx) => {
            const item = items[itemIdx];
            const x = row.items.slice(0, row.items.indexOf(itemIdx)).reduce((s, j) => s + itemWidths[j], 0);
            return (
              <g key={item.key} transform={`translate(${x}, 0)`}>
                <LegendSwatch item={item} theme={theme} markerScale={markerScale} />
                {item.hatchIndex != null && item.type === 'rect' && (
                  <rect x={0} y={-Math.round(rs / 2)} width={rs} height={rs} fill={`url(#${hatchPrefix}-${itemIdx})`} />
                )}
                <text x={textXForType(item.type, markerScale)} y={LEGEND_SPACING.textDy} fill={theme.legendTextColor} fontSize={legendFontSize} fontFamily={fonts.label}>
                  {item.label}
                </text>
              </g>
            );
          })}
        </g>
        );
      })}
    </g>
  );
}

export default ChartLegend;
